/**
 * TenantFormModal.jsx
 *
 * Reusable form modal for both creating and editing tenants from the
 * Admin → Companies tab. The mode is determined by whether `tenant` is
 * passed: if truthy → edit mode (PATCH via updateTenantSettings); if
 * null → create mode (POST via adminCreateTenant).
 *
 * Why a single component for both:
 *   - Same fields, same validation, same look-and-feel
 *   - One place to keep the "what an admin can change about a tenant"
 *     concept in sync with the backend
 *
 * Fields:
 *   - Company name (required)
 *   - Owner email + name + initial password (create only — once a
 *     tenant exists you change owner credentials elsewhere)
 *   - Business type
 *   - Plan
 *   - Status (edit only — new tenants always start "active")
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";
import {
  FiX, FiAlertCircle, FiSave, FiPlus, FiRefreshCw,
} from "react-icons/fi";

const BIZ_TYPES = [
  { key: "agency",  icon: "🎯", labelKey: "business_type_agency"  },
  { key: "store",   icon: "🛒", labelKey: "business_type_store"   },
  { key: "service", icon: "💼", labelKey: "business_type_service" },
];

// Plan IDs must match the keys in plansConfig.js. Keeping the list
// hardcoded here is deliberate — these three are the canonical plans
// and changing them is a deliberate product decision, not something
// that should silently expand based on Firestore data.
const PLANS = [
  { id: "free",       label: "Free"       },
  { id: "pro",        label: "Pro"        },
  { id: "enterprise", label: "Enterprise" },
];

export default function TenantFormModal({ open, tenant, onClose, onSaved }) {
  const { t, isRTL }   = useLanguage();
  const { darkMode }   = useDarkMode();
  const isEdit         = !!tenant;

  // ── form state ──────────────────────────────────────────
  const [companyName,   setCompanyName]   = useState("");
  const [ownerName,     setOwnerName]     = useState("");
  const [ownerEmail,    setOwnerEmail]    = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [businessType,  setBusinessType]  = useState("agency");
  const [planId,        setPlanId]        = useState("free");
  const [status,        setStatus]        = useState("active");

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  // Reset/seed the form whenever the modal opens or the target tenant
  // changes. Using `open` as the trigger means closing and reopening
  // gives a fresh form, which is what the user expects.
  useEffect(() => {
    if (!open) return;
    if (tenant) {
      setCompanyName(tenant.name || "");
      setBusinessType(tenant.businessType || "agency");
      setPlanId(tenant.planId || tenant.plan || "free");
      setStatus(tenant.status || "active");
    } else {
      setCompanyName("");
      setOwnerName("");
      setOwnerEmail("");
      setOwnerPassword("");
      setBusinessType("agency");
      setPlanId("free");
      setStatus("active");
    }
    setError("");
  }, [open, tenant]);

  // ── Tailwind tokens ─────────────────────────────────────
  const inputCls = `w-full border rounded-xl px-4 py-2.5 outline-none text-sm transition-colors ${
    darkMode
      ? "bg-gray-900 border-gray-700 text-white focus:border-blue-500 placeholder-gray-500"
      : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 placeholder-gray-400"
  }`;
  const labelCls = `block text-xs font-medium mb-1.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`;
  const headingCls = darkMode ? "text-white" : "text-gray-900";
  const cardCls = darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";

  if (!open) return null;

  // ── submit ──────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Lightweight client-side checks. The server validates these too,
    // so this is purely a UX latency optimisation.
    if (!companyName.trim()) {
      setError(t("admin_tenant_company_name"));
      return;
    }
    if (!isEdit) {
      if (!ownerName.trim() || !ownerEmail.trim() || ownerPassword.length < 8) {
        setError(t("min_password"));
        return;
      }
    }

    setSaving(true);
    try {
      if (isEdit) {
        // We only update fields the admin actually changed. Sending
        // unchanged values is harmless but bumps `updatedAt` for no
        // reason and can race with concurrent edits.
        const updates = {};
        if (businessType !== (tenant.businessType || "agency")) updates.businessType = businessType;
        if (planId       !== (tenant.planId || tenant.plan))    updates.planId       = planId;
        if (status       !== (tenant.status || "active"))       updates.status       = status;

        // Even if updates is empty we still close the modal — the user
        // hit save, that's an explicit "I'm done" signal.
        if (Object.keys(updates).length > 0) {
          const fn = httpsCallable(functions, "updateTenantSettings");
          await fn({ tenantId: tenant.id, updates });
        }
        onSaved?.({ mode: "edit", tenantId: tenant.id });
      } else {
        const fn = httpsCallable(functions, "adminCreateTenant");
        const res = await fn({
          companyName: companyName.trim(),
          ownerName:   ownerName.trim(),
          ownerEmail:  ownerEmail.trim(),
          ownerPassword,
          businessType,
          planId,
          status,
        });
        onSaved?.({ mode: "create", tenantId: res.data?.tenantId });
      }
      onClose?.();
    } catch (err) {
      setError(err.message || t("error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          dir={isRTL ? "rtl" : "ltr"}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className={`${cardCls} border rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-700/40">
              <h2 className={`text-lg font-semibold ${headingCls}`}>
                {isEdit ? t("admin_edit_tenant") : t("admin_add_tenant")}
              </h2>
              <button onClick={onClose} type="button"
                className={`p-2 rounded-xl ${darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">

              {error && (
                <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-400">
                  <FiAlertCircle className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Company name (always editable) */}
              <div>
                <label className={labelCls}>{t("admin_tenant_company_name")} *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  className={inputCls}
                  required
                  // Edit mode currently doesn't support renaming — the
                  // tenant slug is derived from the name and renaming
                  // would create a stale slug. Disable to prevent
                  // accidental changes that wouldn't actually save.
                  disabled={isEdit}
                />
              </div>

              {/* Create-only fields: owner credentials */}
              {!isEdit && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>{t("admin_tenant_owner_name")} *</label>
                      <input
                        type="text"
                        value={ownerName}
                        onChange={e => setOwnerName(e.target.value)}
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>{t("admin_tenant_owner_email")} *</label>
                      <input
                        type="email"
                        value={ownerEmail}
                        onChange={e => setOwnerEmail(e.target.value)}
                        className={inputCls}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t("admin_tenant_owner_password")} *</label>
                    <input
                      type="password"
                      value={ownerPassword}
                      onChange={e => setOwnerPassword(e.target.value)}
                      className={inputCls}
                      minLength={8}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                </>
              )}

              {/* Business type — visual button picker, same UX as the
                  evaluation modal so admins recognize the pattern */}
              <div>
                <label className={labelCls}>{t("admin_tenant_business_type")}</label>
                <div className="grid grid-cols-3 gap-2">
                  {BIZ_TYPES.map(b => (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => setBusinessType(b.key)}
                      className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-sm font-medium transition-colors ${
                        businessType === b.key
                          ? "bg-blue-600 border-blue-500 text-white"
                          : darkMode
                            ? "bg-gray-700/40 border-gray-600 text-gray-300 hover:border-blue-500/50"
                            : "bg-white border-gray-300 text-gray-700 hover:border-blue-400"
                      }`}
                    >
                      <span className="text-xl">{b.icon}</span>
                      <span>{t(b.labelKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Plan + status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("admin_tenant_plan")}</label>
                  <select
                    value={planId}
                    onChange={e => setPlanId(e.target.value)}
                    className={inputCls}
                  >
                    {PLANS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
                {/* Status only shown in edit mode — new tenants are
                    always created as "active". */}
                {isEdit && (
                  <div>
                    <label className={labelCls}>{t("admin_tenant_status")}</label>
                    <select
                      value={status}
                      onChange={e => setStatus(e.target.value)}
                      className={inputCls}
                    >
                      <option value="active">{t("status_active")}</option>
                      <option value="suspended">{t("suspended")}</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-700/40">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                    darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {t("admin_cancel")}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${
                    saving ? "bg-gray-500/20 text-gray-400 cursor-not-allowed"
                           : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {saving ? <FiRefreshCw className="animate-spin" /> : (isEdit ? <FiSave /> : <FiPlus />)}
                  {isEdit ? t("admin_save") : t("admin_create")}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
